
import { useState } from 'react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { 
    X, 
    Clock, 
    CheckCircle2, 
    XCircle, 
    AlertCircle, 
    Timer, 
    Loader2, 
    Activity,
    RefreshCw,
    Search,
    ChevronRight,
    Calendar,
    Hash
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { WorkflowRunSummary } from '@/types/workflow-replay';
import { getStatusVariant, formatDuration } from '@/types/workflow-replay';

interface RunSelectorDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    runs: WorkflowRunSummary[];
    selectedRunId: string | null;
    isLoading: boolean;
    onSelectRun: (runId: string) => void;
    onRefresh: () => void;
}

/**
 * Drawer for selecting a workflow run to replay.
 * Shows runs with timestamp, status, duration, and metrics.
 */
export function RunSelectorDrawer({
    isOpen,
    onClose,
    runs,
    selectedRunId,
    isLoading,
    onSelectRun,
    onRefresh,
}: RunSelectorDrawerProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    // Filter runs based on search and status
    const filteredRuns = runs.filter((run) => {
        // Status filter
        if (statusFilter && run.status !== statusFilter) {
            return false;
        }
        // Search filter - search in ID, input_summary
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            if (
                !run.id.toLowerCase().includes(query) &&
                !(run.input_summary?.toLowerCase().includes(query))
            ) {
                return false;
            }
        }
        return true;
    });

    // Get counts by status
    const statusCounts = runs.reduce((acc, run) => {
        acc[run.status] = (acc[run.status] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const handleSelectRun = (runId: string) => {
        onSelectRun(runId);
        onClose();
    };

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                                {/* Header */}
                                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex-shrink-0">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                                                <Activity className="h-5 w-5 text-indigo-600" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-semibold text-slate-900">
                                                    Select Workflow Run
                                                </h2>
                                                <p className="text-sm text-slate-500">
                                                    {runs.length} runs available • Select a run to replay
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={onRefresh}
                                                disabled={isLoading}
                                                className="gap-1.5"
                                            >
                                                <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
                                                Refresh
                                            </Button>
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                            >
                                                <X size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Filters Bar */}
                                <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-4 flex-shrink-0">
                                    {/* Search */}
                                    <div className="relative flex-1 max-w-md">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <Input
                                            placeholder="Search runs..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-9 h-9"
                                        />
                                    </div>

                                    {/* Status filter pills */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setStatusFilter(null)}
                                            className={cn(
                                                'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
                                                statusFilter === null
                                                    ? 'bg-slate-900 text-white'
                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            )}
                                        >
                                            All ({runs.length})
                                        </button>
                                        {statusCounts.completed && (
                                            <button
                                                onClick={() => setStatusFilter('completed')}
                                                className={cn(
                                                    'px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1',
                                                    statusFilter === 'completed'
                                                        ? 'bg-green-600 text-white'
                                                        : 'bg-green-50 text-green-700 hover:bg-green-100'
                                                )}
                                            >
                                                <CheckCircle2 className="h-3 w-3" />
                                                Completed ({statusCounts.completed})
                                            </button>
                                        )}
                                        {statusCounts.failed && (
                                            <button
                                                onClick={() => setStatusFilter('failed')}
                                                className={cn(
                                                    'px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1',
                                                    statusFilter === 'failed'
                                                        ? 'bg-red-600 text-white'
                                                        : 'bg-red-50 text-red-700 hover:bg-red-100'
                                                )}
                                            >
                                                <XCircle className="h-3 w-3" />
                                                Failed ({statusCounts.failed})
                                            </button>
                                        )}
                                        {statusCounts.running && (
                                            <button
                                                onClick={() => setStatusFilter('running')}
                                                className={cn(
                                                    'px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1',
                                                    statusFilter === 'running'
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                                )}
                                            >
                                                <Loader2 className="h-3 w-3" />
                                                Running ({statusCounts.running})
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Run List */}
                                <div className="flex-1 overflow-y-auto">
                                    {isLoading ? (
                                        <div className="flex items-center justify-center h-full">
                                            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                                        </div>
                                    ) : filteredRuns.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full text-slate-500">
                                            <AlertCircle className="h-12 w-12 mb-3 text-slate-300" />
                                            <p className="text-lg font-medium">No runs found</p>
                                            <p className="text-sm">
                                                {searchQuery || statusFilter
                                                    ? 'Try adjusting your filters'
                                                    : 'No workflow runs have been recorded yet'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-slate-100">
                                            {filteredRuns.map((run) => (
                                                <RunRow
                                                    key={run.id}
                                                    run={run}
                                                    isSelected={run.id === selectedRunId}
                                                    onClick={() => handleSelectRun(run.id)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
        </SlideDrawer>
    );
}

/**
 * Individual run row component.
 */
function RunRow({
    run,
    isSelected,
    onClick,
}: {
    run: WorkflowRunSummary;
    isSelected: boolean;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className={cn(
                'w-full px-6 py-4 flex items-center gap-4 text-left transition-colors',
                'hover:bg-slate-50 focus:bg-slate-50 focus:outline-none',
                isSelected && 'bg-indigo-50 hover:bg-indigo-100'
            )}
        >
            {/* Status Icon */}
            <div className="flex-shrink-0">
                <StatusIcon status={run.status} />
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    {/* Status Badge */}
                    <Badge
                        variant={getStatusVariant(run.status)}
                        className="h-5"
                    >
                        {run.status}
                    </Badge>

                    {/* Trace indicator */}
                    {run.has_trace && (
                        <Badge variant="outline" className="h-5 text-[10px] border-green-300 text-green-700 bg-green-50">
                            OTEL Trace
                        </Badge>
                    )}

                    {/* Current step if running */}
                    {run.status === 'running' && run.current_step_id && (
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                            @ {run.current_step_id}
                        </span>
                    )}
                </div>

                {/* Input summary if available */}
                {run.input_summary && (
                    <p className="text-sm text-slate-600 truncate mb-1">
                        {run.input_summary}
                    </p>
                )}

                {/* Metadata row */}
                <div className="flex items-center gap-4 text-xs text-slate-500">
                    {/* Timestamp */}
                    <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatTimestamp(run.created)}
                    </div>

                    {/* Duration */}
                    {run.duration_ms && (
                        <div className="flex items-center gap-1">
                            <Timer className="h-3 w-3" />
                            {formatDuration(run.duration_ms)}
                        </div>
                    )}

                    {/* Run ID (truncated) */}
                    <div className="flex items-center gap-1 text-slate-400">
                        <Hash className="h-3 w-3" />
                        {run.id.substring(0, 8)}...
                    </div>
                </div>
            </div>

            {/* Selection indicator */}
            <div className="flex-shrink-0">
                <ChevronRight className={cn(
                    'h-5 w-5 transition-colors',
                    isSelected ? 'text-indigo-600' : 'text-slate-300'
                )} />
            </div>
        </button>
    );
}

/**
 * Status icon based on run status.
 */
function StatusIcon({ status }: { status: string }) {
    const iconClass = 'h-8 w-8';
    
    switch (status) {
        case 'completed':
            return (
                <div className="p-2 rounded-full bg-green-100">
                    <CheckCircle2 className={cn(iconClass, 'text-green-600')} />
                </div>
            );
        case 'failed':
        case 'timed_out':
            return (
                <div className="p-2 rounded-full bg-red-100">
                    <XCircle className={cn(iconClass, 'text-red-600')} />
                </div>
            );
        case 'running':
        case 'waiting':
        case 'parallel':
        case 'mapping':
        case 'awaiting_child':
            return (
                <div className="p-2 rounded-full bg-blue-100">
                    <Loader2 className={cn(iconClass, 'text-blue-600 animate-spin')} />
                </div>
            );
        case 'cancelled':
            return (
                <div className="p-2 rounded-full bg-slate-100">
                    <AlertCircle className={cn(iconClass, 'text-slate-600')} />
                </div>
            );
        case 'pending':
        default:
            return (
                <div className="p-2 rounded-full bg-slate-100">
                    <Clock className={cn(iconClass, 'text-slate-500')} />
                </div>
            );
    }
}

/**
 * Format timestamp for display.
 */
function formatTimestamp(timestamp: string): string {
    try {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) {
            return 'just now';
        }
        if (diffMins < 60) {
            return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
        }
        if (diffHours < 24) {
            return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        }
        if (diffDays < 7) {
            return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return timestamp;
    }
}

export { RunSelectorDrawer as default };
